import {
  d1CreateNote,
  d1UpdateNote,
  d1DeleteNote,
  d1GetUserNotes,
  d1GetSharedNotes,
  d1GetNoteById,
  d1RequestNotePublic,
  d1WithdrawNotePublic,
} from "../services/d1Client";
import { cleanVerseQuoteText } from "../bible/verseQuote";

const LOCAL_NOTES_KEY = "bible_local_notes";
const DRAFT_KEY_PREFIX = "bible_note_draft_";

// Helper to get local notes
export const getLocalNotes = () => {
  try {
    const raw = localStorage.getItem(LOCAL_NOTES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

// Helper to save local notes
export const setLocalNotes = (notes) => {
  try {
    localStorage.setItem(LOCAL_NOTES_KEY, JSON.stringify(notes));
    window.dispatchEvent(new Event("bible_notes_changed"));
  } catch (e) {
    console.warn("Failed to save local notes", e);
  }
};

// Draft helpers
export const saveNoteDraft = (draftKey, draftData) => {
  try {
    localStorage.setItem(`${DRAFT_KEY_PREFIX}${draftKey}`, JSON.stringify(draftData));
  } catch (e) {}
};

export const getNoteDraft = (draftKey) => {
  try {
    const raw = localStorage.getItem(`${DRAFT_KEY_PREFIX}${draftKey}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const clearNoteDraft = (draftKey) => {
  try {
    localStorage.removeItem(`${DRAFT_KEY_PREFIX}${draftKey}`);
  } catch (e) {}
};

/**
 * Save or update a note
 * Privacy: does NOT store userEmail to prevent privacy leaks on public shares.
 */
export const saveNote = async (noteData, user) => {
  const isCloud = Boolean(user && user.uid && !user.isGuest && user.uid !== "local_guest_user");
  const now = new Date().toISOString();

  // Validate lengths
  const title = (
    noteData.title ||
    `关于 ${noteData.bookName || ""} ${noteData.chapterNumber || 1}:${noteData.verseRange || ""} 的灵修`
  ).slice(0, 200);
  const content = (noteData.content || "").slice(0, 20000);
  const tags = (Array.isArray(noteData.tags) ? noteData.tags : []).slice(0, 10);

  if (isCloud) {
    const docData = {
      userId: user.uid,
      userDisplayName: user.displayName || user.email?.split("@")[0] || "主内读者",
      chapterId: noteData.chapterId || "",
      bookName: noteData.bookName || "",
      chapterNumber: Number(noteData.chapterNumber) || 1,
      verseRange: noteData.verseRange || "",
      verseText: noteData.verseText || "",
      title,
      content,
      tags,
      isPublic: Boolean(noteData.isPublic),
      featured: Boolean(noteData.featured || false),
      updatedAtIso: now,
    };

    try {
      let result;
      if (noteData.id && !noteData.id.startsWith("local_")) {
        result = await d1UpdateNote(noteData.id, docData);
      } else {
        docData.createdAtIso = now;
        result = await d1CreateNote(docData);
      }
      window.dispatchEvent(new Event("bible_notes_changed"));
      if (Boolean(noteData.isPublic) || Boolean(result?.isPublic)) {
        window.dispatchEvent(new Event("bible_shared_notes_changed"));
      }
      return result;
    } catch (cloudErr) {
      console.warn("Failed to save note to Cloudflare D1, saving to local fallback:", cloudErr.message);
      // Fallback to local storage if D1 is unreachable
    }
  }

  // Offline / Local save
  const localNotes = getLocalNotes();
  const id = noteData.id || `local_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  const reviewStatus = noteData.isPublic ? "pending" : "private";
  const newNote = {
    ...noteData,
    id,
    userId: user?.uid || "local_user",
    userDisplayName: user?.displayName || "本地读者",
    title,
    content,
    tags,
    isPublic: false,
    reviewStatus,
    createdAtIso: noteData.createdAtIso || now,
    updatedAtIso: now,
    isLocal: true,
  };

  const index = localNotes.findIndex((n) => n.id === id);
  if (index >= 0) {
    localNotes[index] = newNote;
  } else {
    localNotes.unshift(newNote);
  }
  setLocalNotes(localNotes);
  return newNote;
};

/**
 * Delete a note
 */
export const deleteNote = async (noteId, user) => {
  if (!noteId) return false;

  // Remove from local notes if present
  const localNotes = getLocalNotes();
  const nextLocal = localNotes.filter((n) => n.id !== noteId);
  setLocalNotes(nextLocal);

  if (user && user.uid && !noteId.startsWith("local_")) {
    try {
      await d1DeleteNote(noteId);
      window.dispatchEvent(new Event("bible_notes_changed"));
      return true;
    } catch (e) {
      console.warn("Error deleting note from Cloudflare D1:", e);
      return false;
    }
  }

  window.dispatchEvent(new Event("bible_notes_changed"));
  return true;
};

/**
 * Fetch all notes for current user (local + cloud)
 */
export const getUserNotes = async (userId) => {
  const localNotes = getLocalNotes();
  if (!userId || userId === "local_guest_user" || userId === "local_user") {
    return localNotes;
  }

  try {
    const cloudNotes = await d1GetUserNotes(userId);

    const sortedCloud = (cloudNotes || []).sort((a, b) => {
      const timeA = new Date(a.updatedAtIso || a.createdAtIso || 0).getTime();
      const timeB = new Date(b.updatedAtIso || b.createdAtIso || 0).getTime();
      return timeB - timeA;
    });

    return [...sortedCloud, ...localNotes.filter((ln) => ln.isLocal)];
  } catch (err) {
    console.warn("Failed to fetch notes from Cloudflare D1, fallback to local:", err);
    return localNotes;
  }
};

/**
 * Get notes for a specific chapter
 */
export const getChapterNotes = async (chapterId, userId) => {
  const allNotes = await getUserNotes(userId);
  return allNotes.filter((n) => n.chapterId === chapterId);
};

/**
 * Fetch public shared notes for the community with pagination
 */
export const getSharedNotes = async ({ max = 30, chapterId = "", bypassCache = false } = {}) => {
  try {
    const list = await d1GetSharedNotes({ max, chapterId, bypassCache });
    return (list || []).sort((a, b) => {
      const timeA = new Date(a.updatedAtIso || a.createdAtIso || 0).getTime();
      const timeB = new Date(b.updatedAtIso || b.createdAtIso || 0).getTime();
      return timeB - timeA;
    });
  } catch (e) {
    console.warn("Error fetching shared notes from Cloudflare D1:", e);
    return [];
  }
};

export const getChapterSharedNotes = async (chapterId, { max = 20 } = {}) => {
  if (!chapterId) return [];
  return getSharedNotes({ max, chapterId });
};

/**
 * Fetch a single shared note by ID
 */
export const getSharedNoteById = async (noteId) => {
  if (!noteId) return null;
  const localNotes = getLocalNotes();
  const localMatch = localNotes.find((n) => n.id === noteId);
  if (localMatch) return localMatch;

  try {
    return await d1GetNoteById(noteId);
  } catch (e) {
    console.warn("Error fetching note by id from Cloudflare D1:", e);
  }
  return null;
};

/**
 * Toggle public share of a note (requests review when true, withdraws to private when false)
 */
export const toggleNoteShare = async (noteId, wantPublic, user) => {
  if (!noteId) return false;

  const localNotes = getLocalNotes();
  const nextLocal = localNotes.map((n) =>
    n.id === noteId
      ? {
          ...n,
          isPublic: false,
          reviewStatus: wantPublic ? "pending" : "private",
        }
      : n
  );
  setLocalNotes(nextLocal);

  if (user && user.uid && !noteId.startsWith("local_")) {
    try {
      await d1UpdateNote(noteId, { isPublic: Boolean(wantPublic) });
      window.dispatchEvent(new Event("bible_notes_changed"));
      window.dispatchEvent(new Event("bible_shared_notes_changed"));
      return true;
    } catch (e) {
      console.warn("Error toggling note share in Cloudflare D1:", e);
      return false;
    }
  }
  return true;
};

export const requestNotePublicReview = async (noteId, user) => {
  if (!noteId) return false;
  if (user && user.uid && !noteId.startsWith("local_")) {
    try {
      if (typeof d1RequestNotePublic === "function") {
        await d1RequestNotePublic(noteId);
      } else {
        await d1UpdateNote(noteId, { isPublic: true });
      }
      window.dispatchEvent(new Event("bible_notes_changed"));
      return true;
    } catch (e) {
      console.warn("Error requesting public review:", e);
      return false;
    }
  }
  return toggleNoteShare(noteId, true, user);
};

export const withdrawNotePublicReview = async (noteId, user) => {
  if (!noteId) return false;
  if (user && user.uid && !noteId.startsWith("local_")) {
    try {
      if (typeof d1WithdrawNotePublic === "function") {
        await d1WithdrawNotePublic(noteId);
      } else {
        await d1UpdateNote(noteId, { isPublic: false });
      }
      window.dispatchEvent(new Event("bible_notes_changed"));
      window.dispatchEvent(new Event("bible_shared_notes_changed"));
      return true;
    } catch (e) {
      console.warn("Error withdrawing public share:", e);
      return false;
    }
  }
  return toggleNoteShare(noteId, false, user);
};

/**
 * Migrate local notes to cloud upon login
 */
export const syncLocalNotesToCloud = async (user) => {
  if (!user || !user.uid || user.isGuest || user.uid === "local_guest_user") return 0;
  const localNotes = getLocalNotes();
  if (!localNotes.length) return 0;

  const remainingNotes = [];
  let count = 0;
  for (const n of localNotes) {
    if (n.isLocal || n.id.startsWith("local_")) {
      try {
        const { id, isLocal, ...rest } = n;
        await saveNote(rest, user);
        count++;
      } catch (err) {
        remainingNotes.push(n);
      }
    } else {
      remainingNotes.push(n);
    }
  }

  setLocalNotes(remainingNotes);
  window.dispatchEvent(new Event("bible_notes_changed"));
  return count;
};

/**
 * One-click export notes to Markdown (with YAML frontmatter for Obsidian/Logseq), JSON, or TXT
 */
export const exportNotes = (notes, format = "markdown") => {
  if (!notes || !notes.length) return;

  const dateStr = new Date().toISOString().slice(0, 10);
  let content = "";
  let filename = `bible-notes-${dateStr}`;
  let mimeType = "text/plain;charset=utf-8";

  if (format === "markdown") {
    mimeType = "text/markdown;charset=utf-8";
    filename += ".md";

    content = `# 圣经灵修笔记汇编\n\n> 导出时间：${new Date().toLocaleString()} · 汇编数量：${notes.length} 条笔记\n\n---\n\n`;

    notes.forEach((note, idx) => {
      const ref = `${note.bookName || ""} ${note.chapterNumber || 1}${note.verseRange ? `:${note.verseRange}` : ""}`;
      const noteDate = note.updatedAtIso || note.createdAtIso || dateStr;

      // Obsidian-friendly YAML Frontmatter block
      content += `<!-- Note ${idx + 1} -->\n`;
      content += `\`\`\`yaml\n`;
      content += `title: "${(note.title || ref).replace(/"/g, '\\"')}"\n`;
      content += `reference: "${ref}"\n`;
      content += `date: "${noteDate}"\n`;
      content += `tags: [${(note.tags || []).map((t) => `"${t}"`).join(", ")}]\n`;
      content += `shared: ${Boolean(note.isPublic)}\n`;
      content += `\`\`\`\n\n`;

      content += `## ${idx + 1}. ${note.title || ref}\n\n`;
      content += `**经文出处**：${ref}  \n`;
      content += `**记录时间**：${noteDate}  \n`;
      if (note.tags && note.tags.length > 0) {
        content += `**标签**：${note.tags.map((t) => `\`#${t}\``).join(" ")}  \n`;
      }
      content += `\n> ### 经文摘录\n>\n> ${
        note.verseText
          ? cleanVerseQuoteText(note.verseText).split("\n").join("\n> ")
          : "（无经文摘录）"
      }\n\n`;
      content += `### 灵修心得与随笔\n\n${note.content || "（无内容）"}\n\n---\n\n`;
    });
  } else if (format === "json") {
    mimeType = "application/json;charset=utf-8";
    filename += ".json";
    content = JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        totalNotes: notes.length,
        notes: notes.map(({ id, isLocal, ...rest }) => rest),
      },
      null,
      2
    );
  } else {
    // Plain text
    filename += ".txt";
    notes.forEach((note, idx) => {
      const ref = `${note.bookName || ""} ${note.chapterNumber || 1}${note.verseRange ? `:${note.verseRange}` : ""}`;
      content += `[${idx + 1}] ${note.title || ref}\n`;
      content += `经文：${ref}\n`;
      if (note.tags?.length) content += `标签：${note.tags.join(", ")}\n`;
      if (note.verseText) content += `经文：${cleanVerseQuoteText(note.verseText)}\n`;
      content += `心得：${note.content || ""}\n`;
      content += `日期：${note.updatedAtIso || note.createdAtIso || ""}\n`;
      content += "=".repeat(48) + "\n\n";
    });
  }

  // Trigger browser download
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
